import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TicketService } from 'src/app/services/ticket.service';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
interface Option {
  id: string;
  label: string;
  selected: boolean;
}

interface CategoryOption {
  id: string;
  name: string;
  description: string;
  acceptedTicket: string;
  selected: boolean;
  label: string;
}

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  providers: [MessageService],
})
export class SignupComponent implements OnInit {
  client: string = '';
  link: string = '';
  isCompany: boolean = true;
  selectedOptions: string[] = [];
  selectedCategorie: any;
  options: Option[] = [];
  categorieOptions: CategoryOption[] = [];
  currentStep: number = 1; // Track the current step
  private required = Validators.required;
  labelMapping: Record<string, string> = {
    Restaurant: 'Restaurant',
    Retail: 'Grande distribution',
    Store: 'Magasin / Boutiques',
    Hostel: "Hotel / Maisons d'accueil",
  };
  signupForm: FormGroup = new FormGroup({
    name: new FormControl(null, [this.required]),
    ninea: new FormControl(null, [this.required]),
    address: new FormControl(null, [this.required]),
    ownerFirstName: new FormControl(null, [this.required]),
    ownerLastName: new FormControl(null, [this.required]),
    ownerEmail: new FormControl(null, [this.required]),
    ownerPhone: new FormControl(null, [this.required]),
    password: new FormControl(null, [this.required]),
    passwordConfirm: new FormControl(null, [this.required]),
    tickets: new FormControl<string[] | null>(null, [this.required]),
    role: new FormControl(null, [this.required]),
    countryCode: new FormControl(221, [this.required]),
  });

  signupFormMerchant: FormGroup = new FormGroup({
    name: new FormControl(null, [this.required]),
    ninea: new FormControl(null, [this.required]),
    address: new FormControl(null, [this.required]),
    ownerFirstName: new FormControl(null, [this.required]),
    ownerLastName: new FormControl(null, [this.required]),
    ownerEmail: new FormControl(null, [this.required]),
    ownerPhone: new FormControl(null, [this.required]),
    password: new FormControl(null, [this.required]),
    passwordConfirm: new FormControl(null, [this.required]),
    category: new FormControl(null, [this.required]),
    countryCode: new FormControl(221, [this.required]),
  });

  constructor(
    private router: Router,
    private authService: AuthService,
    private ticketService: TicketService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initClient();
    this.link = `/${this.client}/signin`;
    this.isCompany = this.client !== 'merchant';

    this.client === 'merchant' ? this.loadCategories() : this.loadTickets();
  }

  private initClient(): void {
    const urlSegment = this.router.url.split('/')?.[1];
    this.client = urlSegment || 'default'; // valeur par défaut si jamais
  }

  private loadTickets(): void {
    this.ticketService.getAllTickets().subscribe({
      next: (response) => {
        this.options = response.tickets.map((item: any) => ({
          ...item,
          selected: false,
        }));
      },
      error: (err) => {
        console.error('Erreur lors du chargement des tickets', err);
      },
    });
  }

  private loadCategories(): void {
    this.ticketService.getCategories().subscribe({
      next: (response) => {
        this.categorieOptions = response.categories.map(
          (item: { name: string | number }) => ({
            ...item,
            label: this.labelMapping[item.name] || item.name,
            selected: false,
          })
        );
      },
      error: (err) => {
        console.error('Erreur lors du chargement des catégories', err);
      },
    });
  }

  onSelectionChange(event: Event, option: any): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    
    if (this.client === 'merchant') {
      // For radio buttons (merchant), deselect all other options
      this.categorieOptions.forEach(item => item.selected = false);
      option.selected = true;
      this.updateSelectedCategory(option);
    } else {
      // For checkboxes (company)
      option.selected = isChecked;
      this.updateSelectedOptions();
    }
    
    // Update form controls after selection
    this.updateFormValues();
  }

  private updateSelectedCategory(option: any): void {
    // Store the category name for merchant signup
    this.selectedCategorie = option.id;
  }

  private updateSelectedOptions(): void {
    this.selectedOptions = this.options
      .filter((item) => item.selected)
      .map((item) => item.id);
  }

  onSubmit(): void {
    this.updateFormValues();

    if (this.signupFormMerchant.valid) {
      const signupData = this.buildMerchantSignupData(
        this.signupFormMerchant.value
      );
      this.handleSignup(signupData);
    }

    if (this.signupForm.valid) {
      const formData = this.signupForm.value;

      if (this.isStandardFormComplete(formData)) {
        const signupData = this.buildStandardSignupData(formData);
        this.handleSignup(signupData);
      }
    } else {
      this.markFormFieldsAsTouched(this.signupForm);
    }
  }

  private updateFormValues(): void {
    this.signupFormMerchant.controls['category'].setValue(
      this.selectedCategorie
    );
    this.signupForm.controls['tickets'].setValue(this.selectedOptions);
  }

  private buildMerchantSignupData(formData: any): any {
    return {
      name: formData.name,
      ninea: formData.ninea,
      address: formData.address,
      ownerFirstName: formData.ownerFirstName,
      ownerLastName: formData.ownerLastName,
      ownerEmail: formData.ownerEmail,
      ownerPhone: formData.ownerPhone,
      password: formData.password,
      passwordConfirm: formData.passwordConfirm,
      category: formData.category,
      countryCode: formData.countryCode,
    };
  }

  private buildStandardSignupData(formData: any): any {
    return {
      name: formData.name,
      ninea: formData.ninea,
      address: formData.address,
      ownerFirstName: formData.ownerFirstName,
      ownerLastName: formData.ownerLastName,
      ownerEmail: formData.ownerEmail,
      ownerPhone: formData.ownerPhone,
      password: formData.password,
      passwordConfirm: formData.passwordConfirm,
      tickets: formData.tickets,
      role: formData.role,
      countryCode: formData.countryCode,
    };
  }

  private isStandardFormComplete(formData: any): boolean {
    const requiredFields = [
      'name',
      'ninea',
      'address',
      'ownerFirstName',
      'ownerLastName',
      'ownerEmail',
      'ownerPhone',
      'password',
      'passwordConfirm',
      'tickets',
      'role',
    ];
    return requiredFields.every((field) => !!formData[field]);
  }

  private handleSignup(signupData: any): void {
    this.authService.signup(signupData, this.client).subscribe({
      next: (response: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Inscription réussie. Vous allez recevoir un email de confirmation pour activer votre compte.',
          life: 3000
        });
        
        setTimeout(() => {
          this.router.navigate([this.client + '/signin']);
        }, 2000);
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de l\'inscription, veuillez réessayer.',
          life: 5000
        });
      },
    });
  }

  private markFormFieldsAsTouched(form: FormGroup): void {
    Object.keys(form.controls).forEach((key) => {
      form.get(key)?.markAsTouched();
    });
  }

  // Two-step form navigation methods
  goToNextStep(): void {
    if (this.isStep1Valid()) {
      this.currentStep = 2;
    } else {
      this.markFormFieldsAsTouched(this.isCompany ? this.signupForm : this.signupFormMerchant);
    }
  }

  goToPreviousStep(): void {
    this.currentStep = 1;
  }

  isStep1Valid(): boolean {
    const form = this.isCompany ? this.signupForm : this.signupFormMerchant;
    // Updated fields for Step 1: Entreprise, NInea, address, telephone, avantages sociaux
    const step1Fields = ['name', 'ninea', 'address', 'ownerPhone'];
    
    // Basic field validation
    const fieldsValid = step1Fields.every(field => {
      const control = form.get(field);
      return control && control.valid;
    });
    
    // Check for tickets/category selection
    let selectionValid = true;
    if (this.isCompany) {
      selectionValid = this.selectedOptions.length > 0;
    } else {
      selectionValid = !!form.get('category')?.value;
    }
    
    return fieldsValid && selectionValid;
  }

  isFieldInvalid(fieldName: string): boolean {
    const form = this.isCompany ? this.signupForm : this.signupFormMerchant;
    const control = form.get(fieldName);
    return control ? (control.invalid && (control.dirty || control.touched)) : false;
  }

  passwordMismatch(): boolean {
    const form = this.isCompany ? this.signupForm : this.signupFormMerchant;
    const password = form.get('password')?.value;
    const confirmPassword = form.get('passwordConfirm')?.value;
    return password !== confirmPassword && !!password && !!confirmPassword;
  }
}
